import { withRequestDeadline } from '@/lib/readRequest';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  accountStatusConfig,
  followStatusConfig,
  type BrokerAccount,
  type FollowRecord,
} from '@/lib/brokerAccounts';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import { forexApi, toBrokerAccount, toFollowRecord, type OnboardingSummary } from '@/services/forexApi';
import OnboardingProgress from './OnboardingProgress';
import EmptyState from '@/components/base/EmptyState';

interface Props {
  onAddNew: () => void;
}

const typeConfig = {
  mt4: { icon: 'ri-terminal-box-line', color: '#6366f1', label: 'MT4' },
  mt5: { icon: 'ri-terminal-window-line', color: '#8b5cf6', label: 'MT5' },
  api: { icon: 'ri-key-2-line', color: '#10b981', label: 'API' },
};

export default function AccountList(props: Props) {
  const { accessToken } = useAuth();
  return <AccountListContent key={accessToken || 'signed-out'} {...props} />;
}

function AccountListContent({ onAddNew }: Props) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [followRecords, setFollowRecords] = useState<FollowRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rebindingId, setRebindingId] = useState<string | null>(null);
  const [confirmUnbindId, setConfirmUnbindId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ── Edit credentials states ──
  const [editAccount, setEditAccount] = useState<BrokerAccount | null>(null);
  const [editLogin, setEditLogin] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editServer, setEditServer] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [credentialConsent, setCredentialConsent] = useState(false);
  const [resubmissionRevision, setResubmissionRevision] = useState<string | undefined>();
  const [credentialError, setCredentialError] = useState('');
  const credentialKey = useRef(crypto.randomUUID());

  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState(false);
  const [followError, setFollowError] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const readSequence = useRef(0);
  const releasePending = useRef(false);
  const unconfirmedRef = useRef<string | null>(null);
  const session = useRef(accessToken);
  session.current = accessToken;

  const refresh = useCallback(async () => {
    const request = ++readSequence.current;
    if (!accessToken) { setAccounts([]); setFollowRecords([]); setLoading(false); return; }
    setLoading(true);
    const [accountResult, relationResult] = await Promise.allSettled([
      withRequestDeadline(forexApi.listFollowerAccounts(accessToken)), withRequestDeadline(forexApi.listFollowRelations(accessToken)),
    ]);
    if (request !== readSequence.current) return;
    setLoading(false);
    setReadError(accountResult.status === 'rejected');
    setFollowError(relationResult.status === 'rejected');
    if (accountResult.status === 'fulfilled') {
      const brokerAccounts = accountResult.value.map(toBrokerAccount);
      setAccounts(brokerAccounts);
      if (relationResult.status === 'fulfilled') setFollowRecords(relationResult.value.map(relation => toFollowRecord(relation, brokerAccounts)));
      else setFollowRecords([]);
      if (unconfirmedRef.current) {
        const current = brokerAccounts.find(account => account.id === unconfirmedRef.current);
        if (current) {
          setActionMessage(current.accountStatus === 'released' ? '已核对：账户已释放，历史记录保留' : '已重新读取账户状态，请核对后再操作。');
          unconfirmedRef.current = null; setUnconfirmed(null);
        }
      }
    } else { throw accountResult.reason; }
  }, [accessToken]);

  useEffect(() => {
    setAccounts([]); setFollowRecords([]); setReadError(false); setFollowError(false);
    setActionMessage(''); setActionCode(''); setUnconfirmed(null);
    void refresh().catch(() => {});
    return () => { readSequence.current += 1; };
  }, [refresh]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleUnbind = async (id: string) => {
    if (releasePending.current || unconfirmed === id || !accessToken) return;
    releasePending.current = true;
    const token = accessToken;
    setDeletingId(id); setActionMessage(''); setActionCode('');
    try {
      const released = await withRequestDeadline(forexApi.releaseFollowerAccount(id, accessToken, 'user requested'));
      if (session.current !== token) return;
      setConfirmUnbindId(null);
      setExpandedId(null);
      const confirmed = released.account_status === 'released';
      setActionMessage(confirmed ? '账户已释放，历史记录保留' : '释放请求已返回，请核对账户状态');
      try { await refresh(); }
      catch { if (session.current === token) setActionMessage(confirmed ? '账户已释放，列表更新失败，请重试读取' : '释放请求已返回，列表更新失败，请重试读取'); }
    } catch (error) {
      if (session.current !== token) return;
      setConfirmUnbindId(null);
      const code = error instanceof ApiError ? error.code : '';
      setActionCode(code);
      if (code === 'ACCOUNT_003') setActionMessage('账户仍有关联跟随，请前往我的跟随处理后再释放。');
      else if (code === 'AUTO_ACCOUNT_BUSY') { setExpandedId(id); setActionMessage('账户仍有托管任务，请查看当前接入状态；需先完成 EA 停止或账户退役。本操作不会自动处理。'); }
      else if (!(error instanceof ApiError) || error.status === 0 || error.status >= 500) {
        unconfirmedRef.current = id; setUnconfirmed(id); setActionMessage('暂未确认释放结果，请刷新账户状态后再操作。');
      } else setActionMessage('释放未完成，请核对当前账户状态后重试。');
    } finally {
      releasePending.current = false;
      if (session.current === token) setDeletingId(null);
    }
  };

  const handleRebind = async (id: string) => {
    const account = accounts.find((item) => item.id === id);
    if (!account) return;
    if (import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true') {
      openEditCredentials(account);
      showToast('请补充完整登录凭据并授权托管后重新提交');
      return;
    }
    setRebindingId(id);
    try {
      await forexApi.resubmitFollowerAccount(id, account, accessToken);
      await refresh();
      showToast('已重新提交接入资料');
    } finally {
      setRebindingId(null);
    }
  };

  const openEditCredentials = async (acc: BrokerAccount) => {
    if (import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true' && acc.accountStatus === 'connected') {
      showToast('请先核对 Broker 持仓和未决订单，再释放旧接入后重新提交托管开通');
      return;
    }
    let revision: string | undefined;
    if (acc.accountStatus === 'failed') {
      try {
        const current = await forexApi.onboardingProgress(acc.id, accessToken);
        if (current.can_resubmit !== true || !current.resubmission_revision) { showToast('当前尚不满足重新提交条件，请刷新或联系管理员'); return; }
        revision = current.resubmission_revision;
      } catch { showToast('无法确认重新提交条件，请稍后重试'); return; }
    }
    setResubmissionRevision(revision);
    setCredentialError('');
    setEditAccount(acc);
    setCredentialConsent(false);
    credentialKey.current = crypto.randomUUID();
    setEditLogin(acc.login || '');
    setEditPassword('');
    setEditServer(acc.server || '');
    setShowCreds(false);
  };

  const closeEditCredentials = () => {
    if (savingCreds) return;
    setCredentialConsent(false);
    setResubmissionRevision(undefined);
    setCredentialError('');
    setEditAccount(null);
    setEditLogin('');
    setEditPassword('');
    setEditServer('');
    setShowCreds(false);
  };

  const handleSaveCredentials = async () => {
    if (!editAccount) return;
    if (import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true' && editAccount.accountStatus === 'connected') {
      showToast('请先核对 Broker 持仓和未决订单，再释放旧接入后重新提交托管开通');
      return;
    }
    setSavingCreds(true);
    try {
      const options = import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true'
        ? await forexApi.onboardingOptions(accessToken) : null;
      const supportedServers = options?.platform_servers ?? options?.servers.map(item => ({ ...item, platform: 'mt4' as const }));
      if (options && (!credentialConsent || !editLogin || !editPassword || !supportedServers?.some(item => item.platform === editAccount.type && item.broker === editAccount.brokerName && item.server === editServer))) { showToast('请填写完整凭据、选择受支持服务器并授权托管'); return; }
      const submitted = await forexApi.resubmitFollowerAccount(editAccount.id, {
        ...editAccount,
        login: editLogin,
        password: editPassword,
        server: editServer,
      }, accessToken, options ? {consentVersion:options.consent_version,operationKey:credentialKey.current,expectedResubmissionRevision:resubmissionRevision} : undefined);
      setAccounts(items => items.map(item => item.id === submitted.id ? toBrokerAccount(submitted) : item));
      setEditAccount(null); setEditLogin(''); setEditPassword(''); setCredentialConsent(false); setCredentialError('');
      showToast('已提交，等待审核');
      try { await refresh(); } catch { showToast('已提交，等待审核；账号列表暂未刷新'); }
    } catch (error) {
      setCredentialError(error instanceof ApiError ? error.message : '提交结果暂未确认，请保持当前表单重试');
      if (error instanceof ApiError && ['RESUBMISSION_STALE', 'RESUBMISSION_NOT_ALLOWED', 'AUTO_IDENTITY_MISMATCH'].includes(error.code)) setResubmissionRevision(undefined);
    } finally {
      setSavingCreds(false);
    }
  };

  const updateProgress = useCallback((id: string, value: OnboardingSummary) => {
    setAccounts(items => items.map(account => {
      if (account.id !== id || (value.submission_revision ?? 0) < (account.onboarding?.submission_revision ?? 0)) return account;
      const status = value.stage === 'connected' ? 'connected' : ['queued', 'preparing', 'logging_in', 'verifying', 'canceling'].includes(value.stage) ? 'provisioning' : value.stage === 'awaiting_review' ? 'reviewing' : value.stage === 'failed' ? 'failed' : value.stage === 'status_unknown' ? 'status_unknown' : account.accountStatus;
      if (account.onboarding?.stage === value.stage && account.onboarding?.submission_revision === value.submission_revision) return account;
      return { ...account, accountStatus: status, onboarding: { stage: value.stage, stage_label: value.stage_label, submission_revision: value.submission_revision } };
    }));
  }, []);

  // ── 统计 ──
  const connectedCount = accounts.filter((a) => a.accountStatus === 'connected').length;
  const reviewingCount = accounts.filter((a) => a.accountStatus === 'reviewing' || a.accountStatus === 'provisioning').length;
  const releasedCount = accounts.filter((a) => a.accountStatus === 'released').length;

  // ── 获取账户当前跟随的智能体名 ──
  const getFollowingAgentName = (acc: BrokerAccount): string | null => {
    const record = getAccountActiveFollow(acc.id);
    if (!record) return null;
    return `智能体 #${record.agentId}`;
  };

  const getAccountActiveFollow = (accountId: string): FollowRecord | undefined => {
    return followRecords.find((record) =>
      record.accountId === accountId &&
      ['running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'pending_risk_confirm', 'configuring', 'status_unknown'].includes(record.followStatus),
    );
  };

  return (
    <div className="px-5">
      <p className="mb-3 text-xs text-gray-500">账户记录用于历史查询，当前不提供删除。以下为本次已读取账户。</p>
      {loading && <p role="status" className="mb-3 text-sm">账户读取中…</p>}
      {readError && <p role="alert" className="mb-3 text-sm text-amber-700">账户状态暂不可用{accounts.length > 0 ? ' · 上次读取结果，本次更新失败' : ''}</p>}
      {followError && <p role="alert" className="mb-3 text-sm text-amber-700">跟随状态暂不可用</p>}
      {actionMessage && <div role="status" className="mb-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">{actionMessage}
        {actionCode === 'ACCOUNT_003' && <button className="ml-2 text-indigo-600" onClick={() => navigate('/agent?tab=my-copy')}>前往我的跟随</button>}
      </div>}
      <button className="mb-3 text-sm text-indigo-600" disabled={loading} onClick={() => void refresh().catch(() => {})}>刷新账户状态</button>
      {/* Security notice */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5 flex gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
          <i className="ri-shield-keyhole-line text-amber-500" />
        </div>
        <div>
          <p className="text-amber-700 font-semibold text-sm">交易账户凭据保护</p>
          <p className="text-amber-600 text-xs mt-0.5 leading-relaxed">
            托管开通凭据由受保护的凭据服务管理；人工开通的处理方式以提交页面说明为准。
          </p>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-2xl p-3 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div className="w-8 h-8 flex items-center justify-center rounded-xl mx-auto mb-1.5" style={{ background: '#10B98115' }}>
            <i className="ri-check-double-line text-sm" style={{ color: '#10B981' }} />
          </div>
          <p className="text-gray-900 font-bold text-lg leading-none">{loading || (readError && accounts.length === 0) ? '—' : connectedCount}</p>
          <p className="text-gray-400 text-xs mt-1">已接入</p>
        </div>
        <div className="bg-white rounded-2xl p-3 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div className="w-8 h-8 flex items-center justify-center rounded-xl mx-auto mb-1.5" style={{ background: '#F59E0B15' }}>
            <i className="ri-time-line text-sm" style={{ color: '#F59E0B' }} />
          </div>
          <p className="text-gray-900 font-bold text-lg leading-none">{loading || (readError && accounts.length === 0) ? '—' : reviewingCount}</p>
          <p className="text-gray-400 text-xs mt-1">接入中</p>
        </div>
        <div className="bg-white rounded-2xl p-3 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div className="w-8 h-8 flex items-center justify-center rounded-xl mx-auto mb-1.5" style={{ background: '#9CA3AF15' }}>
            <i className="ri-link-unlink text-sm" style={{ color: '#9CA3AF' }} />
          </div>
          <p className="text-gray-900 font-bold text-lg leading-none">{loading || (readError && accounts.length === 0) ? '—' : releasedCount}</p>
          <p className="text-gray-400 text-xs mt-1">已释放</p>
        </div>
      </div>

      {/* Empty state */}
      {!loading && !readError && accounts.length === 0 && (
        <EmptyState
          icon="ri-link-unlink"
          title="暂无绑定账户"
          subtitle="绑定交易账户后，AI Agent 才能连接您的账户执行自动交易。可选择平台已开放的交易服务器。"
          primaryAction={{
            label: '添加交易账户',
            icon: 'ri-add-line',
            onClick: onAddNew,
          }}
          footer="交易账户凭据的处理方式以提交页面说明为准"
        />
      )}

      {/* Account list */}
      <div className="flex flex-col gap-3 mb-6">
        {accounts.map((acc) => {
          const tc = typeConfig[acc.type] || typeConfig.mt4;
          const sc = accountStatusConfig[acc.accountStatus];
          const isExpanded = expandedId === acc.id;
          const isRebinding = rebindingId === acc.id;
          const isDeleting = deletingId === acc.id;
          const activeFollow = getAccountActiveFollow(acc.id);
          const isFollowing = activeFollow !== undefined;
          const followLabel = activeFollow?.followStatus === 'running'
            ? '跟随中'
            : activeFollow ? followStatusConfig[activeFollow.followStatus].label : null;
          const followingAgentName = getFollowingAgentName(acc);

          return (
            <div
              key={acc.id}
              className="bg-white rounded-2xl overflow-hidden"
              style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}
            >
              {/* Main row */}
              <button
                className="w-full px-4 py-4 flex items-center gap-3 text-left"
                onClick={() => setExpandedId(isExpanded ? null : acc.id)}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-2xl flex-shrink-0"
                  style={{ background: `${tc.color}15` }}
                >
                  <i className={`${tc.icon} text-lg`} style={{ color: tc.color }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-gray-900 font-semibold text-sm truncate">{acc.brokerName}</span>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-md font-medium flex-shrink-0"
                      style={{ background: `${tc.color}15`, color: tc.color }}
                    >
                      {tc.label}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs truncate">****{acc.accountNo.slice(-4)} · {acc.server}</p>
                </div>

                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                    <span className="text-xs font-medium" style={{ color: sc.color }}>
                      {acc.accountStatus === 'connected' ? sc.label : acc.onboarding?.stage_label || sc.label}
                      {acc.accountStatus === 'connected' && followLabel && !followError && (
                        <span className="text-[10px] ml-0.5" style={{ color: '#6366F1' }}>· {followLabel}</span>
                      )}
                    </span>
                  </div>
                  <i className={`text-gray-300 text-base transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                </div>
              </button>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-50">
                  {import.meta.env.VITE_EA_MANAGED_ONBOARDING === 'true' && <OnboardingProgress accountId={acc.id} submissionRevision={acc.onboarding?.submission_revision} followLabel={followLabel} followError={followError} onProgress={updateProgress} onResubmit={() => void openEditCredentials(acc)} />}
                  {/* 状态说明 */}
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl mt-3 mb-4" style={{ background: sc.bg.replace('bg-', '#') === '#' ? '#FFFBEB' : sc.bg === 'bg-green-50' ? '#F0FDF4' : '#F9FAFB' }}>
                    <span className="text-xs font-medium" style={{ color: sc.color }}>
                      {acc.accountStatus === 'connected' ? sc.label : acc.onboarding?.stage_label || sc.label}
                    </span>
                    <span className="text-gray-400 text-[10px]">·</span>
                    <span className="text-gray-500 text-xs">
                      {acc.accountStatus === 'connected' && followError
                        ? '跟随状态待核对'
                        : acc.accountStatus === 'connected' && isFollowing
                        ? `跟随 ${followingAgentName} · ${followLabel}`
                        : sc.description}
                    </span>
                  </div>

                  {/* Account details */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {[
                      { label: '账户余额', value: acc.balance },
                      { label: '可用保证金', value: acc.margin },
                      { label: '绑定日期', value: acc.connectedAt },
                      { label: '服务器', value: acc.server },
                      { label: '账户环境', value: acc.accountEnvironment === 'real' ? '真实账户' : acc.accountEnvironment === 'simulation' ? '模拟账户' : '待核实' },
                      { label: '执行状态', value: acc.executionReadiness?.state === 'ready' ? '可进入逐笔风控' : acc.executionReadiness?.state === 'exit_only' ? '仅允许已有仓位退出' : acc.executionReadiness?.state === 'not_ready' ? '新开仓未就绪' : '状态待核实' },
                    ].map((item) => (
                      <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-gray-400 text-xs mb-1">{item.label}</p>
                        <p className="font-semibold text-sm text-gray-900">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Connected — follow status card (only for connected accounts) */}
                  {(acc.balance === '—' || acc.margin === '—') && (
                    <p className="mb-4 text-xs text-gray-500">资金数据暂不可用</p>
                  )}
                  {acc.accountStatus === 'connected' && (
                    <div className="mb-4">
                      {followError ? <p className="text-xs text-amber-700">跟随状态暂不可用，请刷新后查看</p> : isFollowing ? (
                        <button
                          onClick={() => getAccountActiveFollow(acc.id) ? navigate(`/copy-config?agentId=${getAccountActiveFollow(acc.id)?.agentId}`) : navigate('/agent?tab=my-copy')}
                          className="w-full flex items-center justify-between px-3 py-3 rounded-xl"
                          style={{ background: '#6366F108', border: '1px solid #6366F120' }}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: '#6366F115' }}>
                              <i className="ri-robot-line text-sm" style={{ color: '#6366F1' }} />
                            </div>
                            <div className="text-left">
                              <p className="text-xs text-gray-400 leading-none mb-0.5">当前跟随 · {followLabel}</p>
                              <p className="text-sm font-semibold" style={{ color: '#6366F1' }}>{followingAgentName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-medium" style={{ color: '#6366F1' }}>
                            管理跟单
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/agent')}
                          className="w-full flex items-center justify-between px-3 py-3 rounded-xl"
                          style={{ background: '#F9FAFB', border: '1px solid #F3F4F6' }}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 bg-gray-100">
                              <i className="ri-robot-line text-sm text-gray-400" />
                            </div>
                            <div className="text-left">
                              <p className="text-xs text-gray-400 leading-none mb-0.5">跟单状态</p>
                              <p className="text-sm font-medium text-gray-500">暂无跟随智能体</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-medium text-indigo-500">
                            去选择
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {acc.accountStatus === 'reviewing' && (
                      <>
                        <button
                          onClick={() => openEditCredentials(acc)}
                          className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium flex items-center justify-center gap-1.5 active:bg-gray-200"
                        >
                          <i className="ri-edit-line" />
                          修改凭据
                        </button>
                        <button
                          onClick={() => setConfirmUnbindId(acc.id)}
                          className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-500 text-sm font-semibold flex items-center justify-center gap-1.5"
                        >
                          <i className="ri-link-unlink" />
                          释放
                        </button>
                      </>
                    )}

                    {acc.accountStatus === 'connected' && (
                      <>
                        <button
                          onClick={() => openEditCredentials(acc)}
                          className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium flex items-center justify-center gap-1.5 active:bg-gray-200"
                        >
                          <i className="ri-edit-line" />
                          修改凭据
                        </button>
                        <button
                          onClick={() => setConfirmUnbindId(acc.id)}
                          disabled={isDeleting}
                          className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-500 text-sm font-semibold flex items-center justify-center gap-1.5"
                        >
                          {isDeleting ? (
                            <div className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                          ) : (
                            <>
                              <i className="ri-link-unlink" />
                              释放
                            </>
                          )}
                        </button>
                      </>
                    )}

                    {acc.accountStatus === 'released' && (
                      <>
                        <button
                          onClick={() => handleRebind(acc.id)}
                          disabled={isRebinding}
                          className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                          style={{ background: 'linear-gradient(135deg, #6366f1, #7c3aed)' }}
                        >
                          {isRebinding ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              提交中...
                            </>
                          ) : (
                            <>
                              <i className="ri-refresh-line" />
                              重新绑定
                            </>
                          )}
                        </button>

                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new button */}
      {accounts.length > 0 && (
        <button
          onClick={onAddNew}
          className="w-full py-4 rounded-2xl border-2 border-dashed border-indigo-200 flex items-center justify-center gap-2 text-indigo-400 font-medium text-sm mb-4"
          style={{ background: 'rgba(99,102,241,0.03)' }}
        >
          <i className="ri-add-circle-line text-lg" />
          添加新的交易账户
        </button>
      )}

      {/* ── Edit Credentials Sheet ── */}
      {editAccount && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={closeEditCredentials}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-t-3xl w-full px-5 pt-5 pb-8 max-w-[430px] mx-auto" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <h3 className="text-gray-900 font-bold text-base mb-1">{resubmissionRevision ? '重新提交凭据' : '修改账户凭据'}</h3>
            <p className="text-gray-400 text-xs mb-5">
              {editAccount.brokerName} · {typeConfig[editAccount.type].label}
            </p>

            <div className="mb-4">
              <label className="text-gray-500 text-xs font-medium mb-1.5 block">服务器 / 接入地址</label>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5">
                <i className="ri-server-line text-gray-400 text-sm" />
                <input
                  readOnly={!!resubmissionRevision}
                  disabled={savingCreds}
                  value={editServer}
                  onChange={(e) => (setEditServer(e.target.value), credentialKey.current = crypto.randomUUID())}
                  className="bg-transparent flex-1 text-sm text-gray-900 outline-none placeholder-gray-400"
                  placeholder="例如 ICMarketsSC-Live"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-gray-500 text-xs font-medium mb-1.5 block">登录账号</label>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5">
                <i className="ri-user-line text-gray-400 text-sm" />
                <input
                  disabled={savingCreds}
                  value={editLogin}
                  onChange={(e) => (setEditLogin(e.target.value), credentialKey.current = crypto.randomUUID())}
                  className="bg-transparent flex-1 text-sm text-gray-900 outline-none placeholder-gray-400"
                  placeholder={`${typeConfig[editAccount.type].label} 登录账号`}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-gray-500 text-xs font-medium mb-1.5 block">密码</label>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5">
                <i className="ri-lock-password-line text-gray-400 text-sm" />
                <input
                  type={showCreds ? 'text' : 'password'}
                  disabled={savingCreds}
                  autoComplete="new-password"
                  value={editPassword}
                  onChange={(e) => (setEditPassword(e.target.value), credentialKey.current = crypto.randomUUID())}
                  className="bg-transparent flex-1 text-sm text-gray-900 outline-none placeholder-gray-400"
                  placeholder="交易密码"
                />
                <button onClick={() => setShowCreds((v) => !v)} className="text-gray-400">
                  <i className={`${showCreds ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
            </div>

            {credentialError && <p role="alert" className="text-xs text-red-500 my-2">{credentialError}</p>}
            <div className="flex gap-3 mt-2">
              <button
                onClick={closeEditCredentials}
                className="flex-1 py-3 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm"
              >
                取消
              </button>
              <label className="text-xs flex gap-2"><input type="checkbox" disabled={savingCreds} checked={credentialConsent} onChange={e => setCredentialConsent(e.target.checked)} />授权平台托管凭证并运行 EA（自动开通账号必选）</label>
                  <button
                onClick={handleSaveCredentials}
                disabled={savingCreds || (editAccount.accountStatus === 'failed' && !resubmissionRevision)}
                className="flex-1 py-3 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #6366f1, #7c3aed)' }}
              >
                {savingCreds ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    保存中...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line" />
                    保存凭据
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm unbind modal */}
      {confirmUnbindId && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => { if (!releasePending.current) setConfirmUnbindId(null); }}
        >
          <div
            className="bg-white rounded-t-3xl w-full px-5 py-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-link-unlink text-red-500 text-xl" />
            </div>
            <h3 className="text-gray-900 font-bold text-base text-center mb-2">确认释放账户？</h3>
            <p className="text-gray-400 text-sm text-center mb-6 leading-relaxed">
              释放后，该账户不能继续用于当前平台跟随。历史记录会保留。若仍有跟随关系或托管任务，系统将拒绝释放，请先按提示处理。本操作不会自动停止跟随，也不代表立即平仓。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { if (!releasePending.current) setConfirmUnbindId(null); }}
                className="flex-1 py-3 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm"
              >
                取消
              </button>
              <button
                disabled={!!deletingId || unconfirmed === confirmUnbindId || readError || loading}
                onClick={() => handleUnbind(confirmUnbindId)}
                className="flex-1 py-3 rounded-2xl bg-red-500 text-white font-semibold text-sm"
              >
                确认释放
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-gray-900/90 text-white text-sm px-4 py-2.5 rounded-full backdrop-blur-sm">
          {toast}
        </div>
      )}
    </div>
  );
}
